import type { CompanyInfo } from '@mt/shared';
import type { DuctProductProfile, DuctworkRequest, ParsedDuct, PlateSpec } from '@mt/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ParsedRow {
  lineNo: number;
  raw: string;
  code: string | null;
  name: string;
  spec: string | null;
  unit: string | null;
  quantity: number | null;
  brand: string | null;
  note: string | null;
  /** Tiêu đề nhóm phía trên dòng (đọc dạng lưới) */
  group?: string;
}

export interface Candidate {
  id: number;
  code: string;
  name: string;
  spec: string | null;
  unit: string;
  brand: string | null;
  salePrice: number;
  vatRate: number;
  weightKg: number | null;
  score: number;
  /** Tính được theo tấm (mã có ĐVT Kg + độ dày hoặc tên tấm/tôn) */
  plate?: PlateSpec | null;
  /** Mã ống gió tính được m² (CR-008) */
  duct?: DuctProductProfile | null;
  /** Mã gốc (DEC-225): cùng familyId = cùng hàng, khác hãng */
  familyId?: number | null;
  familyCode?: string | null;
}

export interface SaleRef {
  date: string | null;
  docNo: string | null;
  customerName: string | null;
  quantity: number | null;
  unitPrice: number | null;
  source: 'asia' | 'erp';
  label: string;
}

export interface PriceInfo {
  listPrice: number;
  customerLast: SaleRef | null;
  othersLast: SaleRef | null;
  warnings: string[];
}

export type MatchMethod = 'alias' | 'code' | 'attr' | 'fuzzy' | null;

export interface MatchedRow extends ParsedRow {
  method: MatchMethod;
  score: number;
  productId: number | null;
  candidates: Candidate[];
  /** Vì sao chưa tự chọn mã (vd khách chưa ghi đen hay mạ kẽm) */
  hint: string | null;
  /** Từ 2 mã ngang điểm: gửi AI chọn giúp (resolveRows) */
  ambiguous: boolean;
  prices: PriceInfo | null;
  suggestedPrice: number | null;
  /** Dòng ống gió nhận ra loại + kích thước */
  duct?: ParsedDuct | null;
  /** Mã gốc của mã máy chọn: brands = số hãng trong danh sách; autoBrand = khách không ghi hãng, máy chọn hãng còn hàng nhiều nhất */
  family?: { code: string; brands: number; autoBrand: boolean } | null;
  /** Khách ghi chung "ống" (không hãng): máy chọn mặc định ống kẽm hãng này (Hoa Sen) */
  defaultBrand?: string | null;
  /** Dòng nhiều mã cùng khớp mà máy vẫn điền mã: history = mã đã báo nhiều nhất (customer/all = số lần 12 tháng); guess = chọn tạm mã đầu */
  pick?: { by: 'history' | 'guess'; customer: number; all: number } | null;
}

export interface Capabilities {
  vision: boolean;
  asia: boolean;
  maxLines: number;
  /** Khách mặc định khi chưa chọn khách (mã Asia KV1); null khi danh mục chưa có */
  defaultCustomer: { id: number; code: string; name: string } | null;
}

export const useCapabilities = () =>
  useQuery({ queryKey: ['quick-quote', 'caps'], queryFn: () => api.get<Capabilities>('/quick-quote/capabilities'), staleTime: 5 * 60_000 });

export const parseFile = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.upload<{ source: string; rows: ParsedRow[] }>('/quick-quote/parse-file', form);
};
/** Nhiều file một lần (vd 2 ảnh chụp bảng khối lượng): đọc lần lượt, gộp thành một danh sách, đánh lại số dòng. */
export const MAX_FILES = 10;
export async function parseFiles(files: File[], onError: (file: File, e: Error) => void) {
  const rows: ParsedRow[] = [];
  const names: string[] = [];
  for (const f of files.slice(0, MAX_FILES)) {
    try {
      const r = await parseFile(f);
      for (const row of r.rows) rows.push({ ...row, lineNo: rows.length + 1 });
      names.push(r.source);
    } catch (e) {
      // Một file: báo đúng lỗi của file đó (VD khóa AI sai), không thêm câu chung "Không đọc được dòng hàng nào"
      if (files.length === 1) throw e;
      onError(f, e as Error);
    }
  }
  if (!rows.length) throw new Error(files.length > 1 ? 'Không đọc được dòng hàng nào từ các file đã chọn' : 'Không đọc được dòng hàng nào');
  return { source: names.join(', '), rows };
}
/** Ảnh thông tin công ty dán vào ô Khách hàng → AI chép chữ, server tách trường (null = không nhận ra công ty) */
export const parseCompanyImage = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.upload<{ text: string; info: CompanyInfo | null }>('/quick-quote/parse-company', form);
};
export const parseText = (text: string) => api.post<{ source: string; rows: ParsedRow[] }>('/quick-quote/parse-text', { text });
export const matchRows = (rows: ParsedRow[], customerId: number | null) => api.post<{ rows: MatchedRow[] }>('/quick-quote/match', { rows, customerId });
export interface ResolveResult {
  lineNo: number;
  productId: number | null;
  code: string | null;
  reason: string;
  cached: boolean;
}
/** P05.6: dòng mập mờ → model mạnh chọn giúp (gộp 1 lần, có cache). Chỉ là gợi ý. */
export const resolveRows = (rows: { lineNo: number; raw: string; candidateIds: number[] }[], context: string[]) =>
  api.post<{ results: ResolveResult[]; skipped: string | null }>('/quick-quote/resolve', { rows, context });
export const fetchPrices = (productId: number, customerId: number | null) =>
  api.get<PriceInfo & { suggestedPrice: number | null }>('/quick-quote/prices', { productId, customerId });

export interface CreateItem {
  raw: string;
  productId: number | null;
  productName: string;
  spec: string | null;
  unit: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
  note: string | null;
  plate?: { count: number; lengthM: number; widthM: number; thicknessMm?: number } | null;
  /** Ống gió: server tính SL/ĐVT/đơn giá theo bộ tính CR-008 */
  ductwork?: DuctworkRequest | null;
  /** Nguyên văn tên khách ghi (đối chiếu khi đổi mã) */
  sourceText?: string | null;
  /** NV tự chọn / ra lệnh đổi mã: ghi nhớ ngay cả khi tự lưu */
  confirmed?: boolean;
  /** Mã máy chọn tạm (dòng nhiều mã cùng khớp, chưa báo lần nào), NV chưa chọn lại: không ghi nhớ */
  guess?: boolean;
  /** Dòng ống gió: khóa học theo mẫu (cấp EI, độ dày, vật liệu) */
  learnKey?: string | null;
}
export interface CommandResult {
  changes: { lines: number[]; product: Candidate }[];
  message: string;
  via: 'rule' | 'ai';
}
/** Ô lệnh đổi mã hàng loạt: "xxx -> yyy" (luật) hoặc câu tự nhiên (AI). line = số thứ tự dòng trên bảng (từ 1). */
export const runCommand = (command: string, lines: { line: number; text: string; code: string | null }[]) =>
  api.post<CommandResult>('/quick-quote/command', { command, lines });
export interface SavedQuick {
  id: number;
  code: string;
  version: number;
}
/** learn = ghi nhớ cách gọi tên → mã (chỉ khi NV bấm Lưu; tự lưu thì không) */
export const createQuickQuote = (customerId: number | null, items: CreateItem[], learn = true) =>
  api.post<SavedQuick>('/quick-quote/create', { customerId, items, learn });
/** Tự lưu / lưu lại bản nháp đã tạo từ Báo giá nhanh (ghi đè dòng hàng, giữ điều khoản) */
export const updateQuickQuote = (id: number, version: number, customerId: number | null, items: CreateItem[], learn = false) =>
  api.put<SavedQuick>(`/quick-quote/${id}`, { customerId, version, items, learn });

export interface AsiaStatus {
  enabled: boolean;
  autoSyncHours: number;
  autoSyncTimes: string[];
  products: { count: number; last: SyncRun | null };
  customers: { count: number; last: SyncRun | null };
  /** DEC-086: phiếu SO1 Asia → cơ hội (API cũ chưa có thì undefined) */
  salesOrders?: { count: number; last: SyncRun | null };
  /** DEC-151: Phiếu xuất bán hàng – hợp đồng SO3 → cơ hội Thành công */
  invoices?: { count: number; last: SyncRun | null };
  /** Phút giữa 2 lần đọc lại phiếu SO1/SO3 3 ngày gần nhất (0 = tắt) */
  docSyncMinutes?: number;
}
export interface SyncRun {
  startedAt: string;
  finishedAt: string | null;
  fetched: number;
  created: number;
  updated: number;
  error: string | null;
}
export const useAsiaStatus = () => useQuery({ queryKey: ['asia', 'status'], queryFn: () => api.get<AsiaStatus>('/asia/status'), staleTime: 60_000 });
export const runAsiaSync = (kind: 'products' | 'customers') =>
  api.post<{ fetched: number; created: number; updated: number; skipped: number }>('/asia/sync', { kind });
